import { BaseEntity, Address } from './index';

// ==================== Employee Types ====================

export type EmploymentType = 'full_time' | 'part_time' | 'casual' | 'contractor';
export type EmploymentStatus = 'active' | 'on_leave' | 'terminated';
export type PayFrequency = 'weekly' | 'fortnightly' | 'monthly' | 'quarterly';
export type PayType = 'hourly' | 'salary';
export type TaxScale = 'regular' | 'medicare_levy' | 'no_tax_free' | 'foreign_resident';

export interface Employee extends BaseEntity {
  code: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  mobile?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  address?: Address;
  
  // Employment details
  employmentType: EmploymentType;
  employmentStatus: EmploymentStatus;
  startDate: string;
  endDate?: string;
  jobTitle: string;
  department?: string;
  
  // Pay details
  payType: PayType;
  payRate: number;
  payFrequency: PayFrequency;
  
  // Tax declaration
  tfn?: string;
  taxScale: TaxScale;
  claimTaxFreeThreshold: boolean;
  hasHelpDebt: boolean;
  hasFinancialSupplementDebt: boolean;
  
  // Superannuation
  superFundId?: string;
  superMemberNumber?: string;
  superContributionRate: number;
  
  // Bank account
  bankBsb?: string;
  bankAccountNumber?: string;
  bankAccountName?: string;
  
  // Leave balances
  leaveBalances: LeaveBalance[];
  
  // YTD totals
  ytdTotals?: EmployeeYTDTotals;
}

export interface LeaveBalance {
  type: LeaveType;
  entitlement: number;
  balance: number;
  unit: 'hours' | 'days';
}

export type LeaveType = 'annual' | 'personal' | 'carers' | 'compassionate' | 'parental' | 'long_service';

export const LEAVE_TYPES: { value: LeaveType; label: string }[] = [
  { value: 'annual', label: 'Annual Leave' },
  { value: 'personal', label: 'Personal/Carers Leave' },
  { value: 'carers', label: 'Carers Leave' },
  { value: 'compassionate', label: 'Compassionate Leave' },
  { value: 'parental', label: 'Parental Leave' },
  { value: 'long_service', label: 'Long Service Leave' },
];

export interface EmployeeYTDTotals {
  gross: number;
  tax: number;
  super: number;
  net: number;
}

export interface EmployeeFilterParams {
  status?: EmploymentStatus;
  type?: EmploymentType;
  department?: string;
  search?: string;
}

export const EMPLOYMENT_TYPES: { value: EmploymentType; label: string }[] = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'casual', label: 'Casual' },
  { value: 'contractor', label: 'Contractor' },
];

export const EMPLOYMENT_STATUSES: { value: EmploymentStatus; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'on_leave', label: 'On Leave' },
  { value: 'terminated', label: 'Terminated' },
];

export const PAY_FREQUENCIES: { value: PayFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

export const PAY_TYPES: { value: PayType; label: string }[] = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'salary', label: 'Salary' },
];

export const TAX_SCALES: { value: TaxScale; label: string }[] = [
  { value: 'regular', label: 'Regular' },
  { value: 'medicare_levy', label: 'Medicare Levy Reduction' },
  { value: 'no_tax_free', label: 'No Tax-Free Threshold' },
  { value: 'foreign_resident', label: 'Foreign Resident' },
];

// ==================== Pay Run Types ====================

export type PayRunStatus = 'draft' | 'pending_approval' | 'approved' | 'paid' | 'cancelled';

export interface PayRun extends BaseEntity {
  payRunNumber: string;
  periodStartDate: string;
  periodEndDate: string;
  paymentDate: string;
  status: PayRunStatus;
  totalWages: number;
  totalTax: number;
  totalSuper: number;
  totalNetPay: number;
  employeeCount: number;
  items: PayRunItem[];
  submittedToStpAt?: string;
  markedPaidAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface PayRunItem {
  id: string;
  payRunId: string;
  employeeId: string;
  employeeName: string;
  employeeCode: string;
  
  // Hours and amounts
  ordinaryHours: number;
  ordinaryRate: number;
  ordinaryAmount: number;
  
  overtimeHours: number;
  overtimeRate: number;
  overtimeAmount: number;
  
  // Allowances
  allowances: PayRunAllowance[];
  totalAllowances: number;
  
  // Deductions
  deductions: PayRunDeduction[];
  totalDeductions: number;
  
  // Totals
  gross: number;
  tax: number;
  superContribution: number;
  netPay: number;
}

export interface PayRunAllowance {
  id: string;
  type: string;
  description: string;
  amount: number;
}

export interface PayRunDeduction {
  id: string;
  type: string;
  description: string;
  amount: number;
}

export interface PayRunFilterParams {
  status?: PayRunStatus;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export const PAY_RUN_STATUSES: { value: PayRunStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: 'gray' },
  { value: 'pending_approval', label: 'Pending Approval', color: 'orange' },
  { value: 'approved', label: 'Approved', color: 'blue' },
  { value: 'paid', label: 'Paid', color: 'green' },
  { value: 'cancelled', label: 'Cancelled', color: 'red' },
];

// ==================== Payslip Types ====================

export interface Payslip {
  id: string;
  payRunId: string;
  employeeId: string;
  payRunNumber: string;
  periodStartDate: string;
  periodEndDate: string;
  paymentDate: string;
  generatedAt: string;
  downloadedAt?: string;
  emailedAt?: string;
  item: PayRunItem;
  ytdTotals: EmployeeYTDTotals;
}

// ==================== STP Types ====================

export type STPSubmissionStatus = 'READY' | 'VALIDATION_FAILED' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED';

export interface STPSubmission extends BaseEntity {
  payRunId: string;
  status: STPSubmissionStatus;
  submissionType: string;
  submissionData?: any;
  atoReceiptId?: string;
  atoMessage?: string;
  submittedAt?: string;
  payRun?: {
    id: string;
    payPeriodStart: string;
    payPeriodEnd: string;
    paymentDate: string;
    status: string;
  };
}

export interface STPEvent {
  id: string;
  organizationId: string;
  employeeId: string;
  eventType: string;
  eventDate: string;
  status: string;
}

export const STP_STATUSES: { value: STPSubmissionStatus; label: string; color: string }[] = [
  { value: 'READY', label: 'Ready to lodge', color: 'green' },
  { value: 'VALIDATION_FAILED', label: 'Validation failed', color: 'red' },
  { value: 'SUBMITTED', label: 'Submitted', color: 'blue' },
  { value: 'ACCEPTED', label: 'Accepted by ATO', color: 'green' },
  { value: 'REJECTED', label: 'Rejected', color: 'red' },
];

// ==================== Superannuation Types ====================

export interface SuperFund extends BaseEntity {
  usi: string;
  abn: string;
  name: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  address?: Address;
  isActive: boolean;
}

export interface SuperContribution extends BaseEntity {
  batchId: string;
  payRunId: string;
  payRunNumber: string;
  periodEndDate: string;
  status: 'pending' | 'submitted' | 'accepted' | 'rejected';
  totalAmount: number;
  contributions: SuperContributionItem[];
  submittedAt?: string;
  responseMessage?: string;
}

export interface SuperContributionItem {
  id: string;
  contributionId: string;
  employeeId: string;
  employeeName: string;
  superFundId: string;
  superFundName: string;
  memberNumber: string;
  amount: number;
}

// ==================== Leave Types ====================

export type LeaveApplicationStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';

export interface LeaveApplication extends BaseEntity {
  employeeId: string;
  employeeName: string;
  leaveType: LeaveType;
  startDate: string;
  endDate: string;
  hoursPerDay: number;
  totalHours: number;
  reason?: string;
  status: LeaveApplicationStatus;
  approvedBy?: string;
  approvedAt?: string;
  approvalNotes?: string;
}

export const LEAVE_APPLICATION_STATUSES: { value: LeaveApplicationStatus; label: string; color: string }[] = [
  { value: 'pending', label: 'Pending', color: 'orange' },
  { value: 'approved', label: 'Approved', color: 'green' },
  { value: 'rejected', label: 'Rejected', color: 'red' },
  { value: 'cancelled', label: 'Cancelled', color: 'gray' },
];

// ==================== Timesheet Types ====================

export type TimesheetStatus = 'draft' | 'submitted' | 'approved' | 'rejected';
export type TimesheetEntryType = 'ordinary' | 'overtime' | 'penalty' | 'allowance';

export interface Timesheet extends BaseEntity {
  employeeId: string;
  employeeName: string;
  weekStarting: string;
  status: TimesheetStatus;
  totalHours: number;
  entries: TimesheetEntry[];
  submittedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface TimesheetEntry {
  id: string;
  timesheetId: string;
  date: string;
  entryType: TimesheetEntryType;
  startTime?: string;
  endTime?: string;
  breakMinutes: number;
  hours: number;
  description?: string;
}

export const TIMESHEET_STATUSES: { value: TimesheetStatus; label: string; color: string }[] = [
  { value: 'draft', label: 'Draft', color: 'gray' },
  { value: 'submitted', label: 'Submitted', color: 'blue' },
  { value: 'approved', label: 'Approved', color: 'green' },
  { value: 'rejected', label: 'Rejected', color: 'red' },
];

export const TIMESHEET_ENTRY_TYPES: { value: TimesheetEntryType; label: string }[] = [
  { value: 'ordinary', label: 'Ordinary Hours' },
  { value: 'overtime', label: 'Overtime' },
  { value: 'penalty', label: 'Penalty Rates' },
  { value: 'allowance', label: 'Allowance' },
];

// ==================== ABA Types ====================

export interface ABAFile extends BaseEntity {
  fileName: string;
  payRunId: string;
  payRunNumber: string;
  paymentDate: string;
  description: string;
  totalAmount: number;
  transactionCount: number;
  fileContent: string;
  downloadedAt?: string;
  isValid: boolean;
  validationErrors?: string[];
}

// ==================== Australian Tax Calculations ====================

export interface TaxCalculationInput {
  grossAmount: number;
  payFrequency: PayFrequency;
  taxScale: TaxScale;
  hasHelpDebt: boolean;
  hasFinancialSupplementDebt: boolean;
  claimTaxFreeThreshold: boolean;
  ytdGross?: number;
}

export interface TaxCalculationResult {
  gross: number;
  tax: number;
  medicare: number;
  helpDebt?: number;
  financialSupplement?: number;
  net: number;
}
